import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApiResponse } from '@card-software/nexus-utilities';
import { AssetDepreciationImportProxyService } from '../../../proxies/asset-depreciation-import.proxy.service';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AssetDepreciationImportResult } from '../../../models/monthly-uploads/asset-depreciation-import';
import { MonthlyAssetDepreciation } from '../../../models/monthly-uploads/monthly-asset-depreciation';

@Component({
  selector: 'app-asset-depreciation-tab',
  templateUrl: './asset-depreciation-tab.component.html',
  styleUrl: './asset-depreciation-tab.component.scss',
})
export class AssetDepreciationTabComponent implements OnInit {
  private readonly proxy = inject(AssetDepreciationImportProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly now = new Date();

  month = new FormControl<number | null>(this.now.getMonth() + 1, [Validators.required]);
  year = new FormControl<number | null>(this.now.getFullYear(), [Validators.required]);
  assetId = new FormControl<string | null>(null);
  formGroup = new FormGroup({ month: this.month, year: this.year });

  selectedFile: File | null = null;
  isLoading = signal(false);
  showUploadUi = signal(false);
  rows = signal<MonthlyAssetDepreciation[]>([]);

  monthOptions: { label: string; value: number }[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  yearOptions: { label: string; value: number }[] = (() => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4].map((v) => ({
      label: String(v),
      value: v,
    }));
  })();

  ngOnInit(): void {
    merge(this.month.valueChanges, this.year.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRows());

    this.assetId.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRows());

    this.loadRows();
  }

  canUpload(): boolean {
    return this.month.value !== null && this.year.value !== null;
  }

  canImport(): boolean {
    return this.canUpload() && this.selectedFile !== null;
  }

  onFileChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (!this.isXlsx(file)) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Asset_Depreciation_Invalid_File'),
        this.translateService.instant('Asset_Depreciation_Invalid_File'),
      );
      input.value = '';
      return;
    }
    this.selectedFile = file;
    input.value = '';
  }

  onDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (!this.canUpload()) return;
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!this.isXlsx(file)) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Asset_Depreciation_Invalid_File'),
        this.translateService.instant('Asset_Depreciation_Invalid_File'),
      );
      return;
    }
    this.selectedFile = file;
  }

  onDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
  }

  clearFile(): void {
    this.selectedFile = null;
  }

  import(): void {
    if (!this.canImport() || this.isLoading()) return;

    this.isLoading.set(true);
    this.proxy
      .importMonthly(this.month.value!, this.year.value!, this.selectedFile!)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result: ApiResponse<AssetDepreciationImportResult>) => {
          if (result.success) {
            const totalInserted = result.data?.processedRows ?? 0;
            this.clearFile();
            this.showUploadUi.set(false);
            this.loadRows();
            GlobalMessageUtil.success(
              this.translateService.instant('Asset_Depreciation_Import_Success'),
              `Total inserted: ${totalInserted}`,
            );
          } else {
            GlobalMessageUtil.error(
              this.translateService.instant('Asset_Depreciation_Import_Error'),
              getLocalizedValue(this.translateService, result.errorMessage) ||
                this.translateService.instant('Asset_Depreciation_Import_Error'),
            );
          }
        },
      });
  }

  openUploadUi(): void {
    this.showUploadUi.set(true);
  }

  cancelUploadUi(): void {
    this.clearFile();
    this.showUploadUi.set(false);
  }

  loadRows(): void {
    this.isLoading.set(true);
    this.proxy
      .search({
        assetId: this.assetId.value ?? null,
        year: this.year.value!,
        month: this.month.value!,
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result.success && result.data) {
            this.rows.set(result.data);
          } else {
            this.rows.set([]);
          }
        },
        error: () => {
          this.rows.set([]);
        },
      });
  }

  private isXlsx(f: File): boolean {
    return /\.xlsx$/i.test(f.name);
  }
}
